import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import AvailabilityCalendar from './AvailabilityCalendar';

// A listing priced per week / per month is booked in WHOLE PERIODS only - the customer pays the
// full-period rate, never a pro-rata part period (that's the owner's internal accounting). So a
// week/month listing picks a START DATE + a NUMBER OF WEEKS/MONTHS and the end date is derived;
// a day-priced listing keeps the free date-range calendar.
const PERIOD_DAYS = { week: 7, month: 30 };

// Timezone-safe: work in UTC so toISOString() never rolls the date back a day.
function addDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
const fmt = (ymd) => new Date(`${ymd}T12:00:00`).toLocaleDateString();

export default function PeriodBookingDates({ item, form, setForm, resourceId, monthsShown = 2 }) {
  const unit = item?.priceUnit;
  const periodDays = PERIOD_DAYS[unit];
  const count = form.periodCount || 1;

  // Keep endDate = start + count periods for a week/month listing.
  useEffect(() => {
    if (!periodDays || !form.startDate) return;
    const derivedEnd = addDays(form.startDate, periodDays * count);
    if (form.endDate !== derivedEnd) setForm((p) => ({ ...p, endDate: derivedEnd }));
  }, [periodDays, count, form.startDate, form.endDate, setForm]);

  if (!periodDays) {
    return (
      <AvailabilityCalendar
        resourceId={resourceId}
        mode="range"
        value={{ start: form.startDate, end: form.endDate }}
        onChange={(range) => setForm((prev) => ({ ...prev, startDate: range.start, endDate: range.end }))}
        monthsShown={monthsShown}
      />
    );
  }

  return (
    <>
      <Form.Group className="mb-2" controlId="booking-period-count">
        <Form.Label>How many {unit}s?</Form.Label>
        <Form.Select
          value={count}
          onChange={(e) => setForm((p) => ({ ...p, periodCount: Number(e.target.value) }))}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n} {unit}{n > 1 ? 's' : ''} · {n * periodDays} days
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">
          This listing is rented by the {unit} — pick a start date and the number of {unit}s.
        </Form.Text>
      </Form.Group>
      <Form.Label>Start date</Form.Label>
      <AvailabilityCalendar
        resourceId={resourceId}
        mode="single"
        value={form.startDate}
        onChange={(day) => setForm((prev) => ({ ...prev, startDate: day }))}
        monthsShown={monthsShown}
      />
      {form.startDate && form.endDate && (
        <div className="small text-muted mt-2">
          {fmt(form.startDate)} → {fmt(form.endDate)} ({count} {unit}{count > 1 ? 's' : ''})
        </div>
      )}
    </>
  );
}
